import React from 'react';

import {
  DollarIcon,
  PercentageIcon,
  WalletIcon,
} from 'app/shared/componentsRestyled/coreDesignSystem/icons';

import {
  accountingFormatOptions,
  fixedTwoFractionDigitsExceptIntegerCurrencyOptions,
} from 'constants/loanApplication';
import { PendingChangeKind } from 'constants/pendingChanges';
import { StatusColor, StatusEnum } from 'constants/status';
import { getConditionalArray } from 'utils/data';
import { formatNumberToCurrencyString, formatNumberToFixed2DecimalsExceptNull } from 'utils/number';
import { getModifiedFieldChangeKind } from 'utils/pendingChangeKind';

const PERCENTAGE = 100;

export const SUMMARY_CARDS_NAMES = {
  INCOME: 'Income',
  TOTAL_INCOME: 'Total Income',
  TOTAL_ASSETS: 'Total Assets',
  ASSETS: 'Assets',
  CASH_TO_CLOSE: 'Cash To Close',
  LIABILITIES: 'Monthly Liabilities',
  TOTAL_LIABILITIES: 'Total Liability Payments',
  NEGATIVE_RENTAL_INCOME: 'Negative Rental Income',
  DTI: 'DTI',
  REO: 'REO',
  PROJECTED_RESERVES: 'Projected Reserves',
};

const currencyOptions = {
  ...fixedTwoFractionDigitsExceptIntegerCurrencyOptions,
  ...accountingFormatOptions,
};

// URLA/DU sign convention: positive cash to close = funds the borrower brings (the normal case,
// so neutral), zero/negative = cash back to the borrower; negative reserves = a shortage.
const getCashToCloseIconColor = cashToClose =>
  cashToClose > 0 ? StatusColor[StatusEnum.INFO] : StatusColor[StatusEnum.SUCCESS];

const getProjectedReservesIconColor = projectedReserves =>
  projectedReserves < 0 ? StatusColor[StatusEnum.ERROR] : StatusColor[StatusEnum.SUCCESS];

// Nobody types a total: it only moves because of a change to one of its records.
const getTotalChangeKind = isTotalChanged =>
  isTotalChanged ? PendingChangeKind.RECALCULATED : null;

export const getSummaryInfoConfig = ({
  totalIncomes,
  totalAssets,
  totalLiabilities,
  totalNegativeRentalIncome,
  projectedReserves,
  cashToClose,
  dti,
  isPersonalized,
  isModalContext = false,
  summaryPendingFields = null,
  isIncomesChanged,
  isAssetsChanged,
  isLiabilitiesChanged,
  isNegativeRentalIncomeChanged,
}) => [
  {
    name: isModalContext ? SUMMARY_CARDS_NAMES.TOTAL_INCOME : SUMMARY_CARDS_NAMES.INCOME,
    value: formatNumberToCurrencyString(totalIncomes, currencyOptions),
    icon: <DollarIcon />,
    iconColor: StatusColor[StatusEnum.SUCCESS],
    changeKind: getTotalChangeKind(isIncomesChanged),
  },
  {
    name: isModalContext ? SUMMARY_CARDS_NAMES.TOTAL_ASSETS : SUMMARY_CARDS_NAMES.ASSETS,
    value: formatNumberToCurrencyString(totalAssets, currencyOptions),
    icon: <DollarIcon />,
    iconColor: StatusColor[StatusEnum.SUCCESS],
    changeKind: getTotalChangeKind(isAssetsChanged),
  },
  ...getConditionalArray(!isPersonalized, () => [
    {
      name: SUMMARY_CARDS_NAMES.PROJECTED_RESERVES,
      value: formatNumberToCurrencyString(projectedReserves, currencyOptions),
      icon: <DollarIcon />,
      iconColor: getProjectedReservesIconColor(projectedReserves),
      changeKind: getModifiedFieldChangeKind(summaryPendingFields, 'ProjectedReserves'),
    },
  ]),
  {
    name: SUMMARY_CARDS_NAMES.CASH_TO_CLOSE,
    value: formatNumberToCurrencyString(cashToClose, currencyOptions),
    icon: <DollarIcon />,
    iconColor: getCashToCloseIconColor(cashToClose),
    changeKind: getModifiedFieldChangeKind(summaryPendingFields, 'CashToClose'),
  },
  {
    name: isModalContext ? SUMMARY_CARDS_NAMES.TOTAL_LIABILITIES : SUMMARY_CARDS_NAMES.LIABILITIES,
    value: formatNumberToCurrencyString(totalLiabilities, currencyOptions),
    icon: <WalletIcon />,
    iconColor: StatusColor[StatusEnum.ERROR],
    changeKind: getTotalChangeKind(isLiabilitiesChanged),
  },
  ...getConditionalArray(totalNegativeRentalIncome, () => [
    {
      name: SUMMARY_CARDS_NAMES.NEGATIVE_RENTAL_INCOME,
      value: formatNumberToCurrencyString(totalNegativeRentalIncome, currencyOptions),
      icon: <WalletIcon />,
      iconColor: StatusColor[StatusEnum.ERROR],
      changeKind: getTotalChangeKind(isNegativeRentalIncomeChanged),
    },
  ]),
  ...getConditionalArray(!isPersonalized, () => [
    {
      name: SUMMARY_CARDS_NAMES.DTI,
      value: `${formatNumberToFixed2DecimalsExceptNull(dti * PERCENTAGE)}%`,
      icon: <PercentageIcon />,
      changeKind: getModifiedFieldChangeKind(summaryPendingFields, 'DebtToIncome'),
    },
  ]),
];
